import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'https://multi-schoolsuite.cbthost.com';

const SchoolRegister = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',               // school email
    admin_email: '',          // admin login email
    admin_password: '',
    phone: '',
    address: '',
  });
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setLogo(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Optional: strict Gmail validation for admin email
    if (!formData.admin_email.toLowerCase().endsWith('@gmail.com')) {
      toast.error('Admin email must be a Gmail address (@gmail.com)');
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append('name', formData.name);
    data.append('email', formData.email);
    data.append('admin_email', formData.admin_email);
    data.append('admin_password', formData.admin_password);
    if (formData.phone) data.append('phone', formData.phone);
    if (formData.address) data.append('address', formData.address);
    if (logo) data.append('logo', logo);

    try {
      const response = await fetch(`${API_URL}/schools/register`, {
        method: 'POST',
        body: data,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Registration failed');
      }

      // Navigate to success page with details
      navigate('/registration-success', {
        state: {
          schoolName: formData.name,
          schoolEmail: formData.email,
          regCode: result.reg_code,
          adminEmail: formData.admin_email,
          adminPassword: formData.admin_password,
        },
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 py-12">
      <div className="max-w-md w-full mx-4 space-y-8 p-6 md:p-8 bg-white rounded-2xl shadow-xl">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-primary-100 rounded-full flex items-center justify-center">
              <i className="fas fa-school text-2xl md:text-3xl text-primary-600"></i>
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Register Your School</h2>
          <p className="mt-2 text-sm md:text-base text-gray-600">
            Create your school account to get started
          </p>
        </div>

        {/* Important information panel */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md text-sm text-blue-700 space-y-2">
          <p className="font-semibold">📌 Important Information</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>
              <strong>Payment:</strong> After registration, you will be redirected to the payment portal.
              Your school will be automatically approved once payment is confirmed.
            </li>
            <li>
              <strong>Credits:</strong> All activities use a <strong>pay-as-you-go credit system</strong>.
            </li>
            <li>
              <strong>Offline desktop version:</strong> Create quizzes offline with{' '}
              <a
                href="https://cbthost.com/download"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                cbthost desktop
              </a>.
            </li>
            <li>
              <strong>Guidelines:</strong> Read the full{' '}
              <a
                href="https://cbthost.com/school_management_software"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                SchoolSuite guide
              </a>.
            </li>
            <li>
              <strong>Support:</strong> Issues?{' '}
              <a
                href="https://cbthost.com/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                Contact us
              </a>.
            </li>
          </ul>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* School Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                School Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Sunshine International School"
              />
            </div>

            {/* School Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                School Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="school@example.com"
              />
            </div>

            {/* Admin Email (Gmail only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Email (Login) <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="admin_email"
                required
                value={formData.admin_email}
                onChange={handleChange}
                pattern="[a-zA-Z0-9._%+-]+@gmail\.com$"
                title="Only Gmail addresses are allowed for admin accounts"
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="admin@gmail.com"
              />
              <p className="mt-1 text-xs text-amber-600">
                ⚠️ Strictly use a Gmail address – it is required for staff operations.
              </p>
            </div>

            {/* Admin Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                name="admin_password"
                required
                value={formData.admin_password}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="+1234567890"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                name="address"
                rows="3"
                value={formData.address}
                onChange={handleChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="School address"
              />
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                School Logo
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional. JPEG, PNG or GIF only.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Registering...
              </>
            ) : (
              'Register School'
            )}
          </button>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have a school account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign in
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SchoolRegister;